import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { OrganizationContactCreate } from '@/lib/schemas';

// ============================================================================
// Query Hooks (Phase 5 Batch 3: Organization Contacts)
// ============================================================================

/**
 * Fetches all contacts for a specific organization
 * Phase 7.5.5: Added optional query options parameter to allow custom enabled logic
 */
export function useOrganizationContacts(
  organizationId: string | undefined,
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'> & { generalOnly?: boolean }
) {
  const { generalOnly, ...queryOptions } = options ?? {};
  return useQuery({
    // generalOnly (the client page's Contacts list, R1 AC17) is part of the key so the two reads never share
    // a cache entry; the ['organization-contacts', orgId] prefix still invalidates both.
    queryKey: generalOnly
      ? ['organization-contacts', organizationId, 'general']
      : ['organization-contacts', organizationId],
    queryFn: () => api.organizationContacts.list(organizationId!, { generalOnly }),
    enabled: !!organizationId,
    ...queryOptions, // Allow overriding default options
  });
}

// ============================================================================
// Mutation Hooks (Phase 5 Batch 3: Contact CRUD operations)
// ============================================================================

/**
 * Creates a new organization contact.
 *
 * Phase 9.21i: Added optional project_id to auto-add contact to client scope.
 *
 * v0.14 (2026-05-29): Pass the create payload through whole instead of
 * explicit-picking fields. Explicit-pick was dropping `notes` (cross-cutting
 * hunt S3 + feedback_mutation_hook_field_drift.md). organizationId is a
 * routing-only field and stripped before send.
 */
export function useCreateOrganizationContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      ...data
    }: { organizationId: string } & OrganizationContactCreate) =>
      api.organizationContacts.create(organizationId, data),
    onSuccess: async (response, variables) => {
      // v0.14.5 R2: AWAIT the invalidation so the refetch completes before the
      // mutation settles. Prod repro: POST 201 → onSuccess fired a non-awaited
      // invalidateQueries → the list refetch raced and returned the PRE-INSERT
      // rowset; the UI stayed stale until a hard reload. Awaiting (matches
      // useUpdateInvoice / useDeleteInvoice hygiene) makes the create visible
      // immediately. Predicate key (prefix ['organization-contacts', orgId]),
      // never sole exact:true.
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      // Phase 9.21i: Also invalidate project scope contacts if project_id was provided
      if (variables.project_id) {
        await queryClient.invalidateQueries({ queryKey: ['projects', variables.project_id] });
      }
      toast.success('Contact added successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add contact');
    },
  });
}

/**
 * Updates an organization contact
 * 9.12a: Expanded to support all editable fields (not just is_primary_contact)
 */
export function useUpdateOrganizationContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, contactId, data }: {
      organizationId: string;
      contactId: string;
      data: {
        name?: string;
        email?: string;
        phone?: string;
        title?: string;
        notes?: string;
        is_primary_contact?: boolean;
      }
    }) =>
      api.organizationContacts.update(organizationId, contactId, data),
    onSuccess: async (response, variables) => {
      // v0.14.5 R2: await the invalidation (same stale-list race class as
      // create). On a primary toggle the demote of OTHER rows happens
      // server-side, so the refetch must complete before settle or the list
      // shows two primaries briefly until reload.
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      toast.success('Contact updated successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update contact');
    },
  });
}

/**
 * Deletes an organization contact
 */
export function useDeleteOrganizationContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, contactId }: { organizationId: string; contactId: string }) =>
      api.organizationContacts.delete(organizationId, contactId),
    onSuccess: async (response, variables) => {
      // v0.14.5 R2: await the invalidation so a delete is reflected without a
      // manual reload (same race class as create/update).
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      toast.success('Contact deleted successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete contact');
    },
  });
}
